import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ProductCard } from "@/components/ProductCard";
import { iconForCategory } from "@/lib/categoryIcons";
import { useAuth } from "@/context/AuthContext";
import { SITE } from "@/lib/constants";
import { ShoppingBasket, MapPin, Heart, RotateCcw } from "lucide-react";

export default function Home() {
  usePageMeta("Groceries & Essentials Delivered in Minutes", `Order groceries, fruits, vegetables, dairy and daily essentials from Nicee. Cash on Delivery to ${SITE.pincodes}.`);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reorder, setReorder] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/bestsellers").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role === "customer") {
      api.get("/orders/reorder").then(({ data }) => setReorder(data)).catch(() => {});
    } else {
      setReorder([]);
    }
  }, [user]);

  return (
    <div>
      {/* Delivery band */}
      <section className="bg-gradient-to-b from-brand/25 to-transparent">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink" data-testid="delivery-pill">
            <MapPin className="h-3.5 w-3.5 text-brand" /> Delivering to {SITE.pincodes}
          </span>
        </div>
      </section>

      {/* Category grid */}
      {categories.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Shop by Category</h2>
          <div className="mt-6 grid grid-cols-4 gap-x-3 gap-y-5 sm:grid-cols-6 lg:grid-cols-8">
            {categories.map((c, i) => {
              const Icon = iconForCategory(c.name);
              return (
                <motion.button
                  key={c.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.05, 0.4) }}
                  onClick={() => navigate(`/products?category=${c.id}`)}
                  className="group flex flex-col items-center gap-1.5"
                  data-testid={`home-category-${c.slug}`}
                >
                  <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl bg-brand/10 p-1 ring-1 ring-brand/15 transition-all group-hover:-translate-y-1 group-hover:ring-brand">
                    {c.image_id ? (
                      <img src={fileUrl(c.image_id)} alt={c.name} className="h-full w-full rounded-xl object-cover" />
                    ) : (
                      <Icon className="h-7 w-7 text-brand" />
                    )}
                  </div>
                  <span className="text-center text-[11px] font-semibold leading-tight text-ink sm:text-xs">{c.name}</span>
                </motion.button>
              );
            })}
          </div>
        </section>
      )}

      {/* Buy It Again */}
      {reorder.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-8" data-testid="reorder-section">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 text-brand">
              <RotateCcw className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Buy It Again</h2>
              <p className="text-sm text-ink-soft">Your favourites, reorder in one tap.</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {reorder.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Products */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <h2 className="font-heading text-2xl font-bold text-ink sm:text-3xl lg:text-4xl">Bestsellers</h2>
          <Link to="/products" className="hidden text-sm font-semibold text-brand hover:underline sm:block">View all →</Link>
        </div>

        {loading ? (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-cream-alt" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border bg-white/60 p-12 text-center">
            <ShoppingBasket className="mx-auto h-10 w-10 text-ink-muted" />
            <p className="mt-4 font-heading text-lg text-ink">Fresh stock is on its way</p>
            <p className="mt-1 text-sm text-ink-muted">New products will appear here soon. Check back shortly!</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {products.slice(0, 10).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* Happy Customers */}
      {gallery.length > 0 && (
        <section className="bg-cream-alt/50 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink">
                <Heart className="h-3.5 w-3.5 text-brand" /> Our Happy Customers
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-ink sm:text-3xl">Loved across {SITE.pincodes}</h2>
              <p className="mt-2 text-ink-soft">Real photos from families who shop with Nicee.</p>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {gallery.map((g, i) => (
                <motion.figure key={g.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }} className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-ambient" data-testid={`home-gallery-${g.id}`}>
                  <div className="aspect-square w-full overflow-hidden bg-cream-alt">
                    <img src={fileUrl(g.image_id)} alt={g.caption || "Happy customer"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                  </div>
                  {g.caption && <figcaption className="p-3 text-sm text-ink-soft line-clamp-2">{g.caption}</figcaption>}
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
